import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Table, Checkbox, Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';

import { Mask, MasksConfig, MaskTemplate } from 'types';
import ListModifier from 'components/organisms/list-modifier';
import { useIntl } from 'common/config/i18n';

const CustomCheckbox = styled(Checkbox)<{ cursor?: string }>`
  ${(props) =>
    props.cursor &&
    `
      & {
        .ant-checkbox-input {
          cursor: ${props.cursor};
        }
      }
    `}
`;

const FileNameBlock = styled.div<{ noTopMargin?: boolean }>`
  margin-top: ${({ noTopMargin }) => (noTopMargin ? 0 : 4)}px;
`;

const ExpandedRowRender = ({
  isAdmin,
  config,
  rowData,
  onEdit,
}: {
  isAdmin: boolean;
  config: MasksConfig;
  rowData: Mask;
  onEdit: (id: number) => void;
}) => {
  const { t } = useIntl();

  const rowColumns: ColumnsType<MaskTemplate> = [
    {
      title: t('table', 'CHART_PASSENGERS'),
      key: 'clientType',
      dataIndex: 'clientType',
    },
  ];

  if (config.mail) {
    rowColumns.push({
      title: t('table', 'CHART_MAIL_CHANNEL'),
      key: 'mail',
      children: [
        {
          title: t('table', 'CHART_DEFAULT_OBJECT'),
          key: 'mailDefaultObject',
          render: (_, cellRecord) => {
            const mailConfig = cellRecord?.mailConfiguration;
            const allDefaultObjects = [
              mailConfig.defaultObject,
              ...(mailConfig.translations?.map((t) => t.defaultObject) || []),
            ].filter((obj) => obj != null && obj !== '');

            if (allDefaultObjects.length === 0) return '-';

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {allDefaultObjects.map((obj, index) => (
                  <span key={index}>{obj}</span>
                ))}
              </div>
            );
          },
        },
        {
          title: t('table', 'CHART_DEFAULT_HEADBAND'),
          key: 'mailDefaultHeadband',
          dataIndex: ['mailConfiguration', 'defaultHeadband'],
        },
        {
          title: t('table', 'CHART_DEFAULT_CHECKED'),
          key: 'mailEnabledByDefault',
          render: (_, cellRecord) => (
            <CustomCheckbox
              checked={cellRecord.mailConfiguration.enabledByDefault}
              cursor="default"
              style={{ cursor: 'default' }}
            />
          ),
        },
        {
          title: t('table', 'CHART_MASK'),
          key: 'mailFileName',
          render: (_, cellRecord) => {
            const mailConfig = cellRecord?.mailConfiguration;
            return (
              <div>
                {!!mailConfig?.fileName && (
                  <div>
                    <FileNameBlock noTopMargin>
                      <Tag color="blue">{mailConfig?.fileName}</Tag>
                      <div>
                        {!!mailConfig?.fileModificationDate && (
                          <Tag>{moment(mailConfig?.fileModificationDate).format('YYYY-MM-DD LTS')}</Tag>
                        )}
                      </div>
                    </FileNameBlock>
                    {
                      // Show mailConfig translation specific détails
                      mailConfig.translations.map(
                        (translation) =>
                          translation.fileName && (
                            <FileNameBlock key={translation.id}>
                              <Tag color="blue">{translation?.fileName}</Tag>
                              <div>
                                {!!translation?.fileModificationDate && (
                                  <Tag>{moment(translation?.fileModificationDate).format('YYYY-MM-DD LTS')}</Tag>
                                )}
                              </div>
                            </FileNameBlock>
                          )
                      )
                    }
                  </div>
                )}
              </div>
            );
          },
        },
      ],
    });
  }

  if (config.sms) {
    rowColumns.push({
      title: t('table', 'CHART_SMS_CHANNEL'),
      key: 'sms',
      children: [
        {
          title: t('table', 'CHART_DEFAULT_CHECKED'),
          key: 'smsEnabledByDefault',
          render: (_, cellRecord) => (
            <CustomCheckbox
              checked={!!cellRecord?.smsConfiguration?.enabledByDefault}
              cursor="default"
              style={{ cursor: 'default' }}
            />
          ),
        },
        {
          title: t('table', 'CHART_MASK'),
          key: 'smsFileName',
          render: (_, cellRecord) => {
            const smsConfig = cellRecord?.smsConfiguration;
            return (
              <div>
                {!!smsConfig?.fileName && (
                  <>
                    <Tag color="blue">{smsConfig?.fileName}</Tag>
                    <>
                      {!!smsConfig?.fileModificationDate && (
                        <Tag>{moment(smsConfig?.fileModificationDate).format('YYYY-MM-DD LTS')}</Tag>
                      )}
                    </>
                  </>
                )}
              </div>
            );
          },
        },
      ],
    });
  }

  if (config.whatsApp) {
    rowColumns.push({
      title: t('table', 'CHART_WHATSAPP_CHANNEL'),
      key: 'whatsApp',
      children: [
        {
          title: t('table', 'CHART_DEFAULT_CHECKED'),
          key: 'whatsAppEnabledByDefault',
          render: (_, cellRecord) => (
            <CustomCheckbox
              checked={!!cellRecord?.whatsappConfiguration?.enabledByDefault}
              cursor="default"
              style={{ cursor: 'default' }}
            />
          ),
        },
        {
          title: t('table', 'CHART_MASK_REFERENCES'),
          key: 'whatsAppRefFileName',
          render: (_, cellRecord) => {
            const whatsappConfig = cellRecord?.whatsappConfiguration;
            return (
              <div>
                {!!whatsappConfig?.refFileName && (
                  <>
                    <Tag color="blue">{whatsappConfig?.refFileName}</Tag>
                    <>
                      {!!whatsappConfig?.refFileModificationDate && (
                        <Tag>{moment(whatsappConfig?.refFileModificationDate).format('YYYY-MM-DD LTS')}</Tag>
                      )}
                    </>
                  </>
                )}
              </div>
            );
          },
        },
        {
          title: t('table', 'CHART_MASK_PREVIEW'),
          key: 'whatsAppMaskFileName',
          render: (_, cellRecord) => {
            const whatsappConfig = cellRecord?.whatsappConfiguration;
            return (
              <div>
                {!!whatsappConfig?.maskFileName && (
                  <>
                    <Tag color="blue">{whatsappConfig?.maskFileName}</Tag>
                    <>
                      {!!whatsappConfig?.maskFileModificationDate && (
                        <Tag>{whatsappConfig?.maskFileModificationDate}</Tag>
                      )}
                    </>
                  </>
                )}
              </div>
            );
          },
        },
      ],
    });
  }

  if (config.flashInfo) {
    rowColumns.push({
      title: t('table', 'CHART_FLASH_INFO'),
      key: 'flashInfo',
      children: [
        {
          title: t('table', 'CHART_DEFAULT_CHECKED'),
          key: 'flashEnabled',
          render: (_, cellRecord) => (
            <CustomCheckbox checked={!!cellRecord?.flashEnabled} cursor="default" style={{ cursor: 'default' }} />
          ),
        },
      ],
    });
  }

  if (isAdmin) {
    rowColumns.push({
      title: t('table', 'COLUMN_EDIT'),
      key: 'edit',
      render: (_, cellRecord: MaskTemplate) => (
        <ListModifier hideDeleteButton hideEditButton={!isAdmin} onEdit={() => onEdit(cellRecord.id)} />
      ),
    });
  }

  return <Table rowKey="id" columns={rowColumns} dataSource={rowData.templates} pagination={false} bordered />;
};

export default ExpandedRowRender;
